/**
 * Canonical File Type Definitions
 *
 * Single source of truth for all supported MIME types, extensions, and size limits.
 *
 * When adding/removing types, edit here AND in the backend files below.
 *
 * @see backend/functions/shared/common.js - assetUtils.SUPPORTED_TYPES
 * @see backend/functions/shared/contentValidator.js - allowedTypes, typeExtensionMap
 */

// Extension → MIME type mapping (mirrors backend `typeExtensionMap`)
export const EXTENSION_TO_MIME = {
  // Images
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',

  // Audio
  mp3: 'audio/mpeg',
  wav: 'audio/wav',

  // Video
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  m4v: 'video/x-m4v',

  // 3D Models
  glb: 'model/gltf-binary',
  bin: 'application/octet-stream',
};

// MIME type → accepted file extensions (mirrors backend `typeExtensionMap`)
export const MIME_TO_EXTENSIONS = {
  // Images
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],

  // Audio
  'audio/mpeg': ['.mp3'],
  'audio/wav': ['.wav'],
  'audio/x-wav': ['.wav'],
  'audio/wave': ['.wav'],

  // Video
  'video/mp4': ['.mp4', '.mov', '.m4v'],
  'video/quicktime': ['.mp4', '.mov', '.m4v'],
  'video/x-m4v': ['.mp4', '.mov', '.m4v'],
  'video/mov': ['.mp4', '.mov', '.m4v'],

  // 3D Models
  'model/gltf-binary': ['.glb'],
  'application/octet-stream': ['.glb', '.bin'],
};

// All supported MIME types (mirrors backend `SUPPORTED_TYPES` keys + `allowedTypes`)
export const SUPPORTED_MIME_TYPES = Object.keys(MIME_TO_EXTENSIONS);

// Image MIME types (for UI filtering)
export const IMAGE_MIME_TYPES = SUPPORTED_MIME_TYPES.filter((t) => t.startsWith('image/'));

// Video MIME types (for UI filtering)
export const VIDEO_MIME_TYPES = SUPPORTED_MIME_TYPES.filter((t) => t.startsWith('video/'));

// Audio MIME types (for UI filtering)
export const AUDIO_MIME_TYPES = SUPPORTED_MIME_TYPES.filter((t) => t.startsWith('audio/'));

// 3D Model MIME types (for UI filtering)
export const MODEL_MIME_TYPES = SUPPORTED_MIME_TYPES.filter(
  (t) => t.startsWith('model/') || t === 'application/octet-stream'
);

// Collect all unique extensions for each category (derived from MIME_TO_EXTENSIONS)
function collectExtensions(mimeTypes) {
  const set = new Set();
  for (const mime of mimeTypes) {
    const exts = MIME_TO_EXTENSIONS[mime];
    if (exts) exts.forEach((ext) => set.add(ext));
  }
  return [...set];
}

// Unique file extensions organized by category (for client-side validation)
export const IMAGE_EXTENSIONS = collectExtensions(IMAGE_MIME_TYPES); // ['.png', '.jpg', '.jpeg', '.webp']
export const VIDEO_EXTENSIONS = collectExtensions(VIDEO_MIME_TYPES); // ['.mp4', '.mov', '.m4v']
export const AUDIO_EXTENSIONS = collectExtensions(AUDIO_MIME_TYPES); // ['.mp3', '.wav']
export const MODEL_EXTENSIONS = collectExtensions(MODEL_MIME_TYPES); // ['.glb', '.bin']

/**
 * Check if a filename has a valid extension for a given category.
 * @param {string} filename - The file name to check
 * @param {string} category - 'image', 'video', 'audio', or 'model'
 * @returns {boolean}
 */
export function hasValidExtension(filename, category) {
  if (!filename) return false;
  const lower = filename.toLowerCase();
  const exts = category === 'image' ? IMAGE_EXTENSIONS
    : category === 'video' ? VIDEO_EXTENSIONS
    : category === 'audio' ? AUDIO_EXTENSIONS
    : category === 'model' ? MODEL_EXTENSIONS
    : [];
  return exts.some((ext) => lower.endsWith(ext));
}

/**
 * Infer MIME type from a file.
 * Falls back to extension-based lookup when `file.type` is empty
 * (common on drag-and-drop or mobile browsers).
 */
export function inferMimeType(file) {
  if (!file) return 'application/octet-stream';
  if (file.type) return file.type;
  const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
  return EXTENSION_TO_MIME[ext] || 'application/octet-stream';
}

/**
 * Get the accept attribute string for a file input element,
 * filtering by optional category (image, video, audio, model).
 */
export function getAcceptString(category) {
  let types;
  if (category === 'image') types = IMAGE_MIME_TYPES;
  else if (category === 'video') types = VIDEO_MIME_TYPES;
  else if (category === 'audio') types = AUDIO_MIME_TYPES;
  else if (category === 'model') types = MODEL_MIME_TYPES;
  else types = SUPPORTED_MIME_TYPES;

  // Include both MIME types and their extensions for maximum browser compatibility
  const accept = [];
  for (const mime of types) {
    accept.push(mime);
    const exts = MIME_TO_EXTENSIONS[mime];
    if (exts) accept.push(...exts);
  }
  return accept.join(',');
}